/**
 * PlanFormDialog — mini dialog tạo / sửa Plan (lịch giao dự kiến).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListLocalPQuery } from '@/store/api/localPApi'
import {
  useCreatePlanMutation,
  useUpdatePlanMutation,
} from '@/store/api/localPPlansApi'
import type { LocalPPlanCreateIn, LocalPPlanOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface PlanFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  initialValue?: LocalPPlanOut | null
  onClose: () => void
  onSaved?: () => void
}

interface PlanFormValues {
  contract_no: string
  plan_date: string
  plan_quantity: number | ''
  note: string
}

const EMPTY: PlanFormValues = {
  contract_no: '',
  plan_date: dayjs().format('YYYY-MM-DD'),
  plan_quantity: '',
  note: '',
}

const schema: yup.ObjectSchema<PlanFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn hợp đồng'),
  plan_date: yup.string().required('Bắt buộc chọn ngày plan'),
  plan_quantity: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập SL', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'SL phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<PlanFormValues>

function fromPlan(p: LocalPPlanOut): PlanFormValues {
  return {
    contract_no: p.contract_no ?? '',
    plan_date: p.plan_date ?? '',
    plan_quantity: p.plan_quantity ?? '',
    note: p.note ?? '',
  }
}

function toApi(v: PlanFormValues): LocalPPlanCreateIn {
  return {
    contract_no: v.contract_no.trim(),
    plan_date: v.plan_date,
    plan_quantity: typeof v.plan_quantity === 'number' ? v.plan_quantity : 0,
    note: v.note.trim() === '' ? null : v.note.trim(),
  }
}

export function PlanFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PlanFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createPlan, { isLoading: creating }] = useCreatePlanMutation()
  const [updatePlan, { isLoading: updating }] = useUpdatePlanMutation()
  const loading = creating || updating

  const { data: contractsList } = useListLocalPQuery({
    is_status: 1,
    page: 1,
    page_size: 200,
  })
  const contractOptions = useMemo(() => contractsList?.items ?? [], [contractsList])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PlanFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset(fromPlan(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          await updatePlan({ planId: initialValue.id, body: payload }).unwrap()
          snack.success('Đã cập nhật plan')
        } else {
          await createPlan(payload).unwrap()
          snack.success('Đã tạo plan')
        }
        onSaved?.()
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật plan thất bại' : 'Tạo plan thất bại'),
        )
      }
    },
    () => snack.error('Vui lòng kiểm tra lại các trường còn lỗi'),
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Sửa Plan' : 'Tạo Plan mới'}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {fixedContractNo ? (
            <TextField
              label="Hợp đồng"
              size="small"
              value={fixedContractNo}
              disabled
              fullWidth
            />
          ) : (
            <Controller
              control={control}
              name="contract_no"
              render={({ field }) => {
                const selected =
                  contractOptions.find((c) => c.contract_no === field.value) ??
                  (field.value
                    ? {
                        contract_no: field.value,
                        supplier_name: null,
                        cp_code: null,
                      }
                    : null)
                return (
                  <Autocomplete
                    size="small"
                    options={contractOptions}
                    value={selected as (typeof contractOptions)[number] | null}
                    onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                    getOptionLabel={(opt) =>
                      opt
                        ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                        : ''
                    }
                    isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                    disabled={isEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Hợp đồng *"
                        error={!!errors.contract_no}
                        helperText={errors.contract_no?.message}
                      />
                    )}
                  />
                )
              }}
            />
          )}
          <Controller
            control={control}
            name="plan_date"
            render={({ field }) => (
              <DatePicker
                label="Ngày plan *"
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : null}
                onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.plan_date,
                    helperText: errors.plan_date?.message,
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="plan_quantity"
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value === '' ? '' : field.value}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                }
                label="Số lượng plan (kg) *"
                type="number"
                size="small"
                fullWidth
                error={!!errors.plan_quantity}
                helperText={errors.plan_quantity?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                fullWidth
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PlanFormDialog
